import { useRef } from 'react'

import { clearFilters } from 'features/catalog/hooks/catalog-yogiql-sidebar-handlers'
import {
  hydrateCatalogHashBody,
  hydrateCatalogYogiQLBody,
  readCatalogHashBody,
  readCatalogPageState,
  saveCatalogPageState,
} from 'features/catalog/hooks/use-catalog-filter-hash/catalog-hash-body'
import { useCatalogPanel } from 'features/catalog/hooks/use-catalog-panel'
import type { CatalogYogiQLPage } from 'features/catalog/hooks/use-catalog-yogiql-page'
import type { CatalogService } from 'features/catalog/service'
import { useCatalogFiltersStore } from 'features/catalog/store'
import type { CatalogQueryPanel } from 'features/catalog/types'
import { useCatalogQueryStore } from 'features/catalog/yogiql-store'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { globalStateToConditions } from 'features/feedback/feedback-query'
// bully ignore[no-cross-feature-private-imports: the shared v2 store-projection seam use-catalog-filter-hydration.ts already imported for this overlay. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { applyGlobalFilterStateToPanel } from 'features/filters/hooks/use-publish-panels-to-global-filters'
import type { FilterOption } from 'features/filters/types'
import type { FilterStateV2 } from 'types/project'

/** What the filter-hash lifecycle reads, writes and edits: the v2 store, or
 *  under `enhanced_dialect` the YogiQL query store. Nothing runs until `ready`.
 *  Effects read it without deps, so actions may close over only stable
 *  values (panel ids, the codec) and must write through stores. */
export type CatalogFilterSeam = {
  ready: boolean
  mode: 'v2' | 'yogiql'
  /** The panel as v2, for the writer's "any filters?" test. */
  panel: ChartPanel
  /** The saved state, keying the writer's save. */
  writeKey: unknown
  load: (projectId: string, key: string) => Promise<Record<string, unknown>>
  hydrate: (body: Record<string, unknown>, key: string) => void
  save: (projectId: string) => Promise<string>
  clearFilters: () => void
  overlay: (state: FilterStateV2) => void
}

const NO_OPTIONS: FilterOption[] = []

export function useCatalogFilterSeam(
  catalogService: CatalogService,
  page: CatalogYogiQLPage,
): CatalogFilterSeam {
  const { flag, codec } = page
  const panel = useCatalogPanel(flag.enabled, codec?.options ?? NO_OPTIONS)
  const hydrateStore = useCatalogFiltersStore((state) => state.hydrate)
  const updatePanel = useCatalogFiltersStore((state) => state.updatePanel)
  const toHashPayload = useCatalogFiltersStore((state) => state.toHashPayload)
  const query = useCatalogQueryStore((state) => state.panels[0]!)
  // The key a query was hydrated from; saving it unchanged reuses that key,
  // so a legacy key is never re-saved and a refused one never falls back.
  const hydratedRef = useRef<{ query: CatalogQueryPanel; key: string } | null>(
    null,
  )

  if (!flag.enabled) {
    return {
      ready: flag.settled,
      mode: 'v2',
      panel,
      writeKey: panel,
      load: (projectId, key) =>
        readCatalogHashBody(
          catalogService,
          projectId,
          key,
          page.whenHubOptions,
        ),
      hydrate: (body) => hydrateCatalogHashBody(body, hydrateStore),
      save: (projectId) =>
        catalogService.saveFilterState(projectId, {
          page: 'catalog',
          filterString: JSON.stringify({ v2: toHashPayload() }),
        }),
      clearFilters: () => updatePanel(panel.id, { filters: [] }),
      overlay: (state) =>
        updatePanel(panel.id, applyGlobalFilterStateToPanel(state)),
    }
  }

  return {
    ready: flag.settled && codec !== null,
    mode: 'yogiql',
    panel,
    writeKey: query,
    load: (projectId, key) =>
      readCatalogPageState(catalogService, projectId, key),
    hydrate: (body, key) => {
      const held = hydrateCatalogYogiQLBody(body, codec!)
      hydratedRef.current = held ? { query: held, key } : null
    },
    save: (projectId) => {
      const { panels, activePanelId } = useCatalogQueryStore.getState()
      const hydrated = hydratedRef.current
      if (hydrated && panels[0] === hydrated.query) {
        return Promise.resolve(hydrated.key)
      }
      const state = { panels, activePanelId }
      return saveCatalogPageState(
        catalogService,
        projectId,
        state,
        codec!.options,
      )
    },
    clearFilters: () => clearFilters(query.id),
    overlay: (state) =>
      useCatalogQueryStore
        .getState()
        .setFilters(query.id, globalStateToConditions(state, codec!)),
  }
}
